import {
  SendMessageUrl,
  GetMessagesFromUserUrl,
  UpdateMessageUrl,
  GetAllMessagesUrl } from '../utils/communicationUrls'
import {
  genericPostRequestToApi,
  genericGetRequestToApi,
  genericPutRequestToApi } from '../utils/genericRequests'

import {
  get
} from 'lodash'

import uuid4 from 'uuid/v4'
import { MakeOfferSetUrl } from '../utils/clientUrls'

/**
 * Send message to the list of receivers
 * @param {Object} data - message data
 * @param {Array<String>} data.to - array of receivers
 * @param {String} data.message - message
 * @param {String=} data.propertyId - property id
 */
const sendMessage = (data) => {
  return genericPostRequestToApi(SendMessageUrl(), data)
}

/**
 * Send message to the list of receivers
 * @param {Object} data - message data
 * @param {Array<String>} data.to - array of receivers
 * @param {String} data.message - message
 * @param {String=} data.propertyId - property id
 * @param {Number} data.amount - amount
 */
const sendOfferMessage = ({ to, address, agent, propertyId, clientPhone }) => {
  const message = `Hello ${agent}, <br> I'd like to make an offer on ${address.trim()}. Please call me at ${clientPhone} to discuss`
  return sendMessage({
    to,
    message,
    subject: 'New offer',
    type: 'offer',
    metadata: {
      data: {
        thread: {
          id: uuid4(),
          subject: 'New Buy Offer',
          subtitle: address.trim(),
          type: 'offer_message',
          property_id: propertyId
        }
      }
    }
  })
}

const setOfferTimestamp = (clientId, favoriteId) => {
  return genericPutRequestToApi(MakeOfferSetUrl(clientId, favoriteId))
}

const sentRentThisMessage = ({ to, address, agent, propertyId, clientPhone }) => {
  const message = `Hello ${agent}, <br> I'd like to rent ${address.trim()}. Please call me at ${clientPhone} to discuss.`
  return sendMessage({
    to,
    message,
    subject: 'New offer',
    type: 'offer',
    metadata: {
      data: {
        thread: {
          id: uuid4(),
          subject: 'New Rent Offer',
          subtitle: address.trim(),
          type: 'offer_message',
          property_id: propertyId
        }
      }
    }
  })
}

/**
 * Receives all messages sent by user
 * @param {String} id - User Id
 */
const getUserMessages = (id) => {
  return genericGetRequestToApi(GetMessagesFromUserUrl(id))
}

/**
 * Receives all users messages
 */
const getAllMessages = async() => {
  const response = await Promise.all([
    genericGetRequestToApi(GetAllMessagesUrl()),
    genericGetRequestToApi(GetAllMessagesUrl(50)),
    genericGetRequestToApi(GetAllMessagesUrl(100))])
  let result = []
  response.forEach(resp => { result = [...result, ...resp.data] })
  return result
}

/**
 * Asks the client for offer
 * @param {Object} data - message data
 * @param {Array<String>} data.to - array of receivers
 * @param {String} data.address - home address
 */
const askClientForOffer = ({ to, address, propertyId }) => {
  const message = `I noticed that ${address} matches 80% or more of your needs and wants. Should we discuss making an offer on this home?`
  return sendMessage({
    to,
    message,
    subject: 'Make Offer',
    metadata: {
      message_type: 'offer',
      data: {
        thread: {
          id: uuid4(),
          subject: 'Make Offer',
          type: 'offer_message',
          subtitle: address.trim(),
          property_id: propertyId
        }
      }
    }
  })
}

/**
 * Asks the client for providing feedback
 * @param {Object} data - message data
 * @param {Array<String>} data.to - array of receivers
 * @param {String} data.address - home address
 */
const askClientForFeedback = ({ to, address, propertyId }) => {
  const message = `I enjoyed viewing ${address.trim()} with you, please provided the required feedback to help decide  whether we should make an offer on this home.`
  return sendMessage({
    message,
    to,
    subject: `Please provide feedback for ${address.trim()}`,
    metadata: {
      message_type: 'feedback_request',
      data: {
        thread: {
          isShownForSender: false,
          canReply: false,
          id: uuid4(),
          subject: 'Provide Feedback',
          type: 'provide_feedback_message',
          subtitle: address.trim(),
          property_id: propertyId
        }
      }
    }
  })
}

const sendMessageAboutHome = ({ to, message, subject, propertyId, address }) => {
  return sendMessage({
    to,
    message,
    subject,
    metadata: {
      data: {
        thread: {
          id: uuid4(),
          subject: 'Question about home',
          type: 'home_message',
          subtitle: address,
          property_id: propertyId
        }
      }
    }
  })
}

const sendRouteSchedule = ({ to, message, subject, routeId, date }, additionalProperties) => {
  return sendMessage({
    to,
    message,
    subject,
    metadata: {
      message_type: 'route_schedule',
      data: {
        thread: {
          canReply: false,
          id: uuid4(),
          subject,
          subtitle: date,
          type: 'route_schedule_message',
          route_id: routeId
        }
      }
    }
  })
}

const markMessageAsRead = (messageId) => {
  return genericPutRequestToApi(UpdateMessageUrl(messageId), { read: true })
}

const createNewThread = ({ to, message, subject, threadId }) => {
  return sendMessage({
    to,
    message,
    subject,
    metadata: {
      data: {
        thread: {
          id: threadId,
          subject,
          type: 'common_message'
        }
      }
    }
  })
}

const calculateThreadsFromMessages = (messages = []) => {
  const threads = {}
  const sortedMessages = messages.sort((a, b) => new Date(a._timestamp) - new Date(b._timestamp))
  sortedMessages.forEach(message => {
    const thread = get(message, 'metadata.data.thread', null)
    if (thread) {
      if (threads[thread.id]) {
        threads[thread.id].messages.push(message)
      } else {
        threads[thread.id] = { messages: [message], thread }
      }
    }
  })
  return Object.keys(threads).map(key => threads[key])
}

export {
  sendMessage,
  sendOfferMessage,
  setOfferTimestamp,
  sentRentThisMessage,
  getUserMessages,
  getAllMessages,
  askClientForOffer,
  sendRouteSchedule,
  askClientForFeedback,
  markMessageAsRead,
  sendMessageAboutHome,
  createNewThread,
  calculateThreadsFromMessages
}
